import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useCreateTaskLink, useDeleteTaskLink } from "@dashboard/hooks/useTaskQuery";
import type { TaskLink } from "@dashboard/api/task";

export type PendingLink = { title: string; url: string };

export interface UseTaskLinksOptions {
    initialLinks: TaskLink[];
    isEditMode: boolean;
    taskId?: number;
    onLocalChange?: () => void;
}

export interface TaskLinksApi {
    items: TaskLink[];
    add: (link: PendingLink) => void;
    remove: (id: number) => void;
    mutationError: string | null;
    isMutating: boolean;
    isPostingLinks: boolean;
    submitError: string | null;
    clearSubmitError: () => void;
    hasUnposted: () => boolean;
    postPending: (newTaskId: number) => Promise<boolean>;
    reset: () => void;
}

export function useTaskLinks({
    initialLinks,
    isEditMode,
    taskId,
    onLocalChange,
}: UseTaskLinksOptions): TaskLinksApi {
    const { t } = useTranslation();
    const [items, setItems] = useState<TaskLink[]>(() => initialLinks);
    const [postedIds, setPostedIds] = useState<Set<number>>(() => new Set());
    const [isPostingLinks, setIsPostingLinks] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);

    const createMutation = useCreateTaskLink();
    const deleteMutation = useDeleteTaskLink();

    const nextOrder = (links: TaskLink[]) =>
        links.reduce((max, l) => Math.max(max, l.order), -1) + 1;

    const add = (pending: PendingLink) => {
        if (isEditMode && taskId !== undefined) {
            const order = nextOrder(items);
            createMutation.mutate(
                { taskId, data: { title: pending.title, url: pending.url, order } },
                { onSuccess: (created) => setItems((p) => [...p, created]) },
            );
        } else {
            const tempId = -Date.now();
            setItems((p) => [
                ...p,
                {
                    id: tempId,
                    taskId: 0,
                    title: pending.title,
                    url: pending.url,
                    order: nextOrder(p),
                    createdById: 0,
                    createdAt: "",
                    updatedAt: "",
                },
            ]);
        }
        onLocalChange?.();
    };

    const remove = (id: number) => {
        if (isEditMode && taskId !== undefined && id > 0) {
            deleteMutation.mutate(
                { id, taskId },
                {
                    onSuccess: () => {
                        setItems((p) => p.filter((l) => l.id !== id));
                        setPostedIds((p) => {
                            if (!p.has(id)) return p;
                            const next = new Set(p);
                            next.delete(id);
                            return next;
                        });
                    },
                },
            );
        } else {
            setItems((p) => p.filter((l) => l.id !== id));
            setPostedIds((p) => {
                if (!p.has(id)) return p;
                const next = new Set(p);
                next.delete(id);
                return next;
            });
        }
        onLocalChange?.();
    };

    const hasUnposted = () => items.some((l) => !postedIds.has(l.id));

    const postPending = async (newTaskId: number): Promise<boolean> => {
        const toPost = items.filter((l) => !postedIds.has(l.id));
        if (toPost.length === 0) return true;
        setIsPostingLinks(true);
        const results = await Promise.allSettled(
            toPost.map((link) =>
                createMutation.mutateAsync({
                    taskId: newTaskId,
                    data: { title: link.title, url: link.url, order: link.order },
                }),
            ),
        );
        setIsPostingLinks(false);

        const tempIdToReal = new Map<number, TaskLink>();
        toPost.forEach((link, i) => {
            const result = results[i];
            if (result.status === "fulfilled") {
                tempIdToReal.set(link.id, result.value);
            }
        });

        if (tempIdToReal.size > 0) {
            setItems((prev) => prev.map((l) => tempIdToReal.get(l.id) ?? l));
            setPostedIds((prev) => {
                const next = new Set(prev);
                for (const real of tempIdToReal.values()) next.add(real.id);
                return next;
            });
        }

        const failedCount = toPost.length - tempIdToReal.size;
        if (failedCount > 0) {
            setSubmitError(t("common.errorSavingLinks"));
            return false;
        }
        return true;
    };

    const reset = () => {
        setSubmitError(null);
        setPostedIds(new Set());
    };

    return {
        items,
        add,
        remove,
        mutationError: createMutation.error?.message || deleteMutation.error?.message || null,
        isMutating: createMutation.isPending || deleteMutation.isPending || isPostingLinks,
        isPostingLinks,
        submitError,
        clearSubmitError: () => setSubmitError(null),
        hasUnposted,
        postPending,
        reset,
    };
}
